import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
} from "@/components/ui/sheet";
import { useToast } from "@/components/ui/use-toast";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, FieldPath } from "react-hook-form";
import { Button } from "@/components/ui/button";
import CompactStepIndicator from "@/components/ui/compact-step-indicator";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { ConsultationStatus } from "@/types/enums";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  useCreateConsultationMutation,
  useUpdateConsultationMutation,
  useSubmitConsultationAnswersMutation,
  useGetQuestionsQuery,
  useCreateMeasurementMutation,
  useCreateBodyAnalysisMutation,
  useUpdateBodyAnalysisMutation,
  useGetBodyAnalysisQuery,
  useCreatePostureAnalysisMutation,
  useUpdatePostureAnalysisMutation,
  useGetPostureAnalysisQuery,
  useCreateGoalMutation,
  useUpdateGoalMutation,
  useGetGoalQuery,
  useAssignMealPlanMutation,
  useAssignWorkoutPlanMutation,
  usePatchConsultationStatusMutation,
} from "@/services/consultationApi";
import { useCreateMealPlansMutation } from "@/services/mealPlansApi";
import { FormProps, ErrorType } from "@/types";
import { Consultation, FormQuestionResponse, AnswerValue, BodyAnalysis, PostureAnalysis, HistoryConsultation } from "@/types/consultation";
import Background from "./steps/background";
import BodyMeasurements, { BODY_PART_BY_KEY } from "./steps/body-measurements";
import BodyAnalysisStep, { sections } from "./steps/body-analysis";
import PostureAnalysisStep from "./steps/posture-analysis";
import GoalStep from "./steps/goal";
import MealPlanStep from "./steps/meal-plan";
import WorkoutPlanStep from "./steps/workout-plan";
import { formatAnswerForSubmission } from "./steps/dynamic-question-renderer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { HistoryFilter } from "./history-filter";

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
  setAction?: (action: "add" | "edit") => void;
  refetch?: () => void;
  openSections?: string[];
  setOpenSections?: (sections: string[]) => void;
  historyData?: HistoryConsultation | null;
  isReadOnly?: boolean;
}

// Placeholder step components - to be replaced with actual step components later
const Step1Component = (props: FormStepProps) => <Background {...props} />;
const Step2Component = (props: FormStepProps) => <BodyMeasurements {...props} />;
const Step3Component = (props: FormStepProps) => <BodyAnalysisStep {...props} />;
const Step4Component = (props: FormStepProps) => <PostureAnalysisStep {...props} />;
const Step5Component = (props: FormStepProps) => <GoalStep {...props} />;
const Step6Component = (props: FormStepProps) => <MealPlanStep {...props} />;
const Step7Component = (props: FormStepProps) => <WorkoutPlanStep {...props} />;

const stepContentComponents = [
  Step1Component,
  Step2Component,
  Step3Component,
  Step4Component,
  Step5Component,
  Step6Component,
  Step7Component,
];

const getStepContent = (step: number, formProps: FormStepProps) => {
  const Component = stepContentComponents[step - 1];
  return Component ? <Component {...formProps} historyData={formProps.historyData} /> : null;
};

const ConsultationForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  data,
  setData,
  refetch,
}: FormProps<Consultation>) => {
  const { toast } = useToast();
  const [activeStep, setActiveStep] = useState(1);
  const [openSections, setOpenSections] = useState<string[]>([]);
  const [showWarning, setShowWarning] = useState(false);
  const [sharedHistoryData, setSharedHistoryData] = useState<HistoryConsultation | null>(null);
  const [createConsultation, { isLoading: isCreating }] = useCreateConsultationMutation();
  const [updateConsultation, { isLoading: isUpdating }] = useUpdateConsultationMutation();

  // Define initial values for the form
  const initialValues: Consultation = {
    id: undefined,
    member_id: undefined,
    member_name: "",
    membership_id: undefined,
    own_member_id: "",
    member_dob: "",
    coach_id: undefined,
    coach_name: "",
    consultation_date: new Date().toISOString(),
    status: ConsultationStatus.Draft,
    notes: "",
  };

  // Define steps configuration
  const steps = useMemo(
    () => [
      { label: "Background", key: 1 },
      { label: "Body Measurements", key: 2 },
      { label: "Body Analysis", key: 3 },
      { label: "Posture Analysis", key: 4 },
      { label: "Goal", key: 5 },
      { label: "Meal Plan", key: 6 },
      { label: "Workout Plan", key: 7 },
    ],
    []
  );

  const form = useForm<Consultation>({
    mode: "all",
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    clearErrors,
    reset,
    trigger,
    watch,
    formState: { isSubmitting, errors, isDirty },
  } = form;
  const watcher = watch();
  console.log({ watcher, errors })
  
  // Reset form when dialog opens/closes or action changes
  useEffect(() => {
    if (!isOpen) return;
    if (action === "edit" && data) {
      const rawData = data as Consultation & {
        height?: number | string;
        weight?: number | string;
      };
      reset({
        ...(data as Consultation),
        member_height: rawData.member_height ?? rawData.height ?? "",
        member_weight: rawData.member_weight ?? rawData.weight ?? "",
      });
    } else if (action === "add") {
      setActiveStep(1);
      clearErrors();
      reset(initialValues);
    }
  }, [action, isOpen, data, reset, clearErrors]);

  // Ensure step index never exceeds steps length
  useEffect(() => {
    if (activeStep > steps.length) {
      setActiveStep(1);
    }
  }, [steps.length]);

  // ==================== HANDLE SAVE BUTTON (ALWAYS VISIBLE) ====================
  const handleSaveStep = async (): Promise<boolean> => {
    let hasError = false;

    // STEP 1: Background & Dynamic Questions
    if (activeStep === 1) {
      const values = form.getValues();

      // Manually validate required IDs — trigger() treats numeric 0 as falsy
      const missingFields: string[] = [];
      if (!values.member_id) missingFields.push("Member");
      if (!values.membership_id) missingFields.push("Membership");
      if (!values.coach_id) missingFields.push("Coach");
      if (!values.consultation_date) missingFields.push("Date");
      if (!values.member_height && values.member_height !== 0) missingFields.push("Height");
      if (!values.member_weight && values.member_weight !== 0) missingFields.push("Weight");

      if (missingFields.length > 0) {
        if (!values.member_id) form.setError("member_id", { message: "Member is required" });
        if (!values.membership_id) form.setError("membership_id", { message: "Membership is required" });
        if (!values.coach_id) form.setError("coach_id", { message: "Coach is required" });
        if (!values.member_height && values.member_height !== 0) form.setError("member_height", { message: "Height is required" });
        if (!values.member_weight && values.member_weight !== 0) form.setError("member_weight", { message: "Weight is required" });
        toast({
          variant: "destructive",
          description: `Please fill mandatory fields: ${missingFields.join(", ")}`,
        });
        return false;
      }

      let currentId = values.id;

      try {
        // Create or Update Consultation
        if (!currentId) {
          if (!values.member_id || !values.coach_id || !values.membership_id || !values.consultation_date) {
            toast({
              variant: "destructive",
              description: "Missing required consultation details",
            });
            return false;
          }

          const createPayload = {
            member_id: Number(values.member_id),
            membership_id: Number(values.membership_id),
            coach_id: Number(values.coach_id),
            consultation_date: values.consultation_date,
            status: values.status,
            org_id: orgId,
            height: values.member_height ? Number(values.member_height) : undefined,
            weight: values.member_weight ? Number(values.member_weight) : undefined,
          };

          const res = await createConsultation(createPayload).unwrap();
          currentId = res.id;
          form.setValue("id", currentId);
          setAction?.("edit");
        } else {
          if (!values.member_id || !values.coach_id || !values.membership_id || !values.consultation_date) {
            toast({
              variant: "destructive",
              description: "Missing required consultation details",
            });
            return false;
          }

          const updatePayload = {
            member_id: Number(values.member_id),
            membership_id: Number(values.membership_id),
            coach_id: Number(values.coach_id),
            consultation_date: values.consultation_date,
            status: values.status,
            meal_plan_id: values.assigned_meal_plan_ids ?? 0,
            workout_plan_id: values.assigned_workout_plan_ids ?? 0,
            height: values.member_height ? Number(values.member_height) : undefined,
            weight: values.member_weight ? Number(values.member_weight) : undefined,
          };
          await updateConsultation({ ...updatePayload, id: values.id, org_id: orgId } as any).unwrap();
        }

        // Submit Answers
        const answers = values.answers || {};
        const answersToSubmit: { question_id: number; question_type: string; answer: AnswerValue }[] = [];

        // ✅ Process each question
        Object.entries(answers).forEach(([qId, answer]) => {
          const question = allQuestions.find((q) => String(q.id) === qId);
          if (!question) return; // Skip if question not found

          // ✅ Use formatter
          const formattedAnswer = formatAnswerForSubmission(
            answer,
            question.question_type
          );

          // ✅ Only add if answer exists
          if (formattedAnswer !== null) {
            answersToSubmit.push({
              question_id: Number(question.id),
              question_type: question.question_type,
              answer: formattedAnswer as AnswerValue,
            });
          }
        });

        // 3. Add missing toggle questions as false
        allQuestions.forEach((q) => {
          if (q.question_type === "toggle" && !answers[String(q.id)]) {
            answersToSubmit.push({
              question_id: Number(q.id),
              question_type: "toggle",
              answer: false as AnswerValue,
            });
          }
        });

        // ✅ DEDUPLICATE before submit
        const deduplicatedAnswers = Array.from(
          new Map(
            answersToSubmit.map((answer) => [answer.question_id, answer])
          ).values()
        );

        // ✅ VALIDATION: Log to console
        console.log(
          " Submitting Answers:",
          JSON.stringify(deduplicatedAnswers, null, 2)
        );

        if (deduplicatedAnswers.length > 0) {
          await submitConsultationAnswers({
            consultation_id: currentId!,
            org_id: orgId,
            answers: deduplicatedAnswers,
          }).unwrap();
        }

        // Only show toast for Step 1 save
        toast({
          description: "Background information saved",
          variant: "success",
        });
        refetch?.();
        return true;
      } catch (error: unknown) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error saving",
          description: typedError?.data?.message || "Failed to save",
        });
        return false;
      }
    }

    // STEP 2: Body Measurements
    if (activeStep === 2) {
      const values = form.getValues();
      const consultationId = values.id;
      const measurement = values.body_measurement || {};

      const mandatoryParts = ["chest", "abdomen"];
      const missingParts: string[] = [];

      mandatoryParts.forEach((part) => {
        if (!measurement[part]) {
          missingParts.push(BODY_PART_BY_KEY[part]?.label || part);
        }
      });

      if (missingParts.length > 0) {
        toast({
          variant: "destructive",
          description: `Please add measurements for: ${missingParts.join(", ")}`,
        });
        return false;
      }

      if (consultationId && Object.keys(measurement).length > 0) {
        try {
          await createMeasurement({
            consultation_id: consultationId,
            org_id: orgId,
            data: measurement,
          }).unwrap();
          
          toast({
            description: "Body measurements saved",
            variant: "success",
          });
          refetch?.();
          return true;
        } catch (error: unknown) {
          const typedError = error as ErrorType;
          toast({
            variant: "destructive",
            title: "Error saving",
            description: typedError?.data?.message || "Failed to save measurements",
          });
          return false;
        }
      }
      return false;
    }

    // STEP 3: Body Analysis
    if (activeStep === 3) {
      const values = form.getValues();
      const consultationId = values.id;
      const bodyAnalysis = values.body_analysis;

      if (consultationId && bodyAnalysis) {
        const age = bodyAnalysis.cardio?.age ?? 
                    bodyAnalysis.endurance?.age ?? 
                    bodyAnalysis.flexibility?.age;

        const missingFields: string[] = [];
        
        if (!age || Number.isNaN(Number(age))) {
          missingFields.push("Age");
        }
        
        sections.forEach((s) => {
          if (!bodyAnalysis[s.key]?.rank) {
            missingFields.push(`${s.title} Rank`);
          }
          if (bodyAnalysis[s.key]?.rate === undefined || Number.isNaN(Number(bodyAnalysis[s.key]?.rate))) {
            missingFields.push(`${s.title} Rate`);
          } else {
            const rateVal = Number(bodyAnalysis[s.key]?.rate);
            if (rateVal < 1 || rateVal > 100) {
              missingFields.push(`${s.title} Rate must be 1-100`);
            }
          }
        });

        if (missingFields.length > 0) {
          toast({
            variant: "destructive",
            description: `Please fill: ${missingFields.join(", ")}`,
          });
          return false;
        }

        const cleanedData: BodyAnalysis = {
          age: Number(age),
          cardio: { 
            rank: bodyAnalysis.cardio?.rank || "",
            rate: Number(bodyAnalysis.cardio?.rate || 0)
          },
          endurance: { 
            rank: bodyAnalysis.endurance?.rank || "",
            rate: Number(bodyAnalysis.endurance?.rate || 0)
          },
          flexibility: { 
            rank: bodyAnalysis.flexibility?.rank || "",
            rate: Number(bodyAnalysis.flexibility?.rate || 0)
          },
        };

        try {
          // Only update if this consultation already has body analysis rows
          const hasExistingData =
            existingBodyAnalysis?.consultation_id === consultationId &&
            existingBodyAnalysis?.data &&
            existingBodyAnalysis.data.length > 0;

          if (hasExistingData) {
            await updateBodyAnalysis({
              consultation_id: consultationId,
              org_id: orgId,
              data: cleanedData,
            }).unwrap();
          } else {
            await createBodyAnalysis({
              consultation_id: consultationId,
              org_id: orgId,
              data: cleanedData,
            }).unwrap();
          }
          
          toast({
            description: "Body analysis saved",
            variant: "success",
          });
          refetch?.();
          return true;
        } catch (error: unknown) {
          const typedError = error as ErrorType;
          toast({
            variant: "destructive",
            title: "Error saving",
            description: typedError?.data?.message || "Failed to save body analysis",
          });
          return false;
        }
      }
      return false;
    }

    // STEP 4: Posture Analysis
    if (activeStep === 4) {
      const values = form.getValues();
      const consultationId = values.id;
      const postureAnalysis = values.posture_analysis;

      if (consultationId && postureAnalysis) {
        const missingFields: string[] = [];
        
        if (!postureAnalysis.overhead_squat_anterior) {
          missingFields.push("Overhead Squat – Anterior");
        }
        if (!postureAnalysis.overhead_squat_lateral) {
          missingFields.push("Overhead Squat – Lateral");
        }
        if (!postureAnalysis.single_leg_anterior) {
          missingFields.push("Single Leg Squat – Anterior");
        }
        if (!postureAnalysis.pulling_pushing_lateral) {
          missingFields.push("Pulling & Pushing – Lateral");
        }

        if (missingFields.length > 0) {
          toast({
            variant: "destructive",
            description: `Please fill: ${missingFields.join(", ")}`,
          });
          return false;
        }

        const cleanedData: PostureAnalysis = {
          overhead_squat_anterior: postureAnalysis.overhead_squat_anterior,
          overhead_squat_lateral: postureAnalysis.overhead_squat_lateral,
          single_leg_anterior: postureAnalysis.single_leg_anterior,
          pulling_pushing_lateral: postureAnalysis.pulling_pushing_lateral,
        };

        try {
          const hasExistingData =
            existingPostureAnalysis?.consultation_id === consultationId &&
            existingPostureAnalysis?.data &&
            existingPostureAnalysis.data.length > 0;

          if (hasExistingData) {
            await updatePostureAnalysis({
              consultation_id: consultationId,
              org_id: orgId,
              data: cleanedData,
            }).unwrap();
          } else {
            await createPostureAnalysis({
              consultation_id: consultationId,
              org_id: orgId,
              data: cleanedData,
            }).unwrap();
          }
          
          toast({
            description: "Posture analysis saved",
            variant: "success",
          });
          refetch?.();
          return true;
        } catch (error: unknown) {
          const typedError = error as ErrorType;
          toast({
            variant: "destructive",
            title: "Error saving",
            description: typedError?.data?.message || "Failed to save posture analysis",
          });
          return false;
        }
      }
      return false;
    }

    // STEP 5: Goals
    if (activeStep === 5) {
      const values = form.getValues();
      const goals = values.goals;

      if (!goals || goals.length === 0) {
        toast({
          variant: "destructive",
          description: "Please select at least one goal",
        });
        return false;
      }

      try {
        const hasExistingGoal = existingGoal?.goals && existingGoal.goals.length > 0;

        if (hasExistingGoal) {
          await updateGoal({
            consultation_id: Number(consultationId),
            org_id: orgId,
            data: goals,
          }).unwrap();
        } else {
          await createGoal({
            consultation_id: Number(consultationId),
            org_id: orgId,
            data: goals,
          }).unwrap();
        }

        toast({
          description: "Goals saved successfully",
          variant: "success",
        });
        refetch?.();
        return true;
      } catch (error: unknown) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error saving",
          description: typedError?.data?.message || "Failed to save goals",
        });
        return false;
      }
    }


    // STEP 6: Meal Plan
    if (activeStep === 6) {
      const values = form.getValues();
      const consultationId = values.id;
      const assignedMealPlanIds = values.assigned_meal_plan_ids;

      if (!consultationId) {
        toast({ variant: "destructive", description: "Consultation ID missing" });
        return false;
      }

      if (!assignedMealPlanIds || assignedMealPlanIds.length === 0) {
        toast({
          variant: "destructive",
          description: "Please select at least one meal plan",
        });
        return false;
      }

      try {
        await assignMealPlan({
          consultation_id: consultationId,
          org_id: orgId,
          data: { meal_plan_ids: assignedMealPlanIds },
        }).unwrap();
        
        toast({
          description: "Meal plan assigned",
          variant: "success",
        });
        refetch?.();
        return true;
      } catch (error: unknown) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error saving",
          description: typedError?.data?.message || "Failed to save meal plan",
        });
        return false;
      }
    }

    // STEP 7: Workout Plan (Final Submit)
    if (activeStep === 7) {
      const values = form.getValues();
      const assignedWorkoutPlanIds = values.assigned_workout_plan_ids;
      const plannedWorkoutIds = (values as any).planned_workout_ids as number[] | undefined;

      if (assignedWorkoutPlanIds && assignedWorkoutPlanIds.length > 0) {
        // Check that all selected workouts have been planned
        const unplannedWorkouts = assignedWorkoutPlanIds.filter(
          (id: number) => !plannedWorkoutIds?.includes(id)
        );

        if (unplannedWorkouts.length > 0) {
          toast({
            variant: "destructive",
            description: "Please plan all selected workout plans before saving. Click 'Plan' on each selected workout.",
          });
          return false;
        }

        try {
          // All workouts are already planned via PlanWorkoutSheet
          // Just update status to awaiting approval
          await patchConsultationStatus({
            consultation_id: values.id!,
            org_id: orgId,
            status: ConsultationStatus.AwaitingApproval,
          }).unwrap();
          
          // FINAL SUCCESS MESSAGE - Only shown once at the very end
          toast({
            description: "Consultation form saved successfully",
            variant: "success",
          });
          
          refetch?.();
          forceClose();
          return true;
        } catch (error: unknown) {
          const typedError = error as ErrorType;
          toast({
            variant: "destructive",
            title: "Error saving",
            description: typedError?.data?.message || "Failed to complete consultation",
          });
          return false;
        }
      } else {
        toast({
          variant: "destructive",
          description: "Please select at least one workout plan",
        });
        return false;
      }
    }
    return false;
  };

  const onSubmit = async (formData: Consultation, event?: React.BaseSyntheticEvent) => {
    if (activeStep !== steps.length) {
      event?.preventDefault();
      return;
    }

    // This is handled by handleSaveStep when on step 7
  };

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all mandatory fields",
    });
  };

  const handleClose = () => {
    if (isDirty) {
      setShowWarning(true);
    } else {
      forceClose();
    }
  };

  const forceClose = () => {
    setShowWarning(false);
    clearErrors();
    setActiveStep(1);
    setAction("add");
    setData(undefined);
    reset(initialValues);
    setOpen(false);
  };

  const [submitConsultationAnswers, { isLoading: isSubmittingAnswers }] = useSubmitConsultationAnswersMutation();
  const [createMeasurement, { isLoading: isSavingMeasurement }] = useCreateMeasurementMutation();
  const [createBodyAnalysis, { isLoading: isSavingBodyAnalysis }] = useCreateBodyAnalysisMutation();
  const [updateBodyAnalysis, { isLoading: isUpdatingBodyAnalysis }] = useUpdateBodyAnalysisMutation();
  const [createPostureAnalysis, { isLoading: isSavingPostureAnalysis }] = useCreatePostureAnalysisMutation();
  const [updatePostureAnalysis, { isLoading: isUpdatingPostureAnalysis }] = useUpdatePostureAnalysisMutation();
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  
  const consultationId = watch("id");
  const { currentData: existingBodyAnalysis, isFetching: isFetchingBodyAnalysis } = useGetBodyAnalysisQuery(
    { consultation_id: Number(consultationId), org_id: orgId },
    { skip: !consultationId || activeStep !== 3 || !isOpen }
  );
  const { currentData: existingPostureAnalysis, isFetching: isFetchingPostureAnalysis } = useGetPostureAnalysisQuery(
    { consultation_id: Number(consultationId), org_id: orgId },
    { skip: !consultationId || activeStep !== 4 || !isOpen }
  );
  const { currentData: existingGoal, isFetching: isFetchingGoal } = useGetGoalQuery(
    { consultation_id: Number(consultationId), org_id: orgId },
    { skip: !consultationId || activeStep !== 5 || !isOpen }
  );

  const [createGoal, { isLoading: isCreatingGoal }] = useCreateGoalMutation();
  const [updateGoal, { isLoading: isUpdatingGoal }] = useUpdateGoalMutation();
  const [assignMealPlan, { isLoading: isAssigningMealPlan }] = useAssignMealPlanMutation();
  const [assignWorkoutPlan, { isLoading: isAssigningWorkoutPlan }] = useAssignWorkoutPlanMutation();
  const [patchConsultationStatus, { isLoading: isPatchingStatus }] = usePatchConsultationStatusMutation();
  const [createMealPlans, { isLoading: isCreatingMealPlan }] = useCreateMealPlansMutation();
  const { data: questionsData } = useGetQuestionsQuery({
    org_id: orgId,
    section_key: ["medical", "nutrition", "exercise"],
  }, { skip: activeStep !== 1 || !isOpen });

  const allQuestions = useMemo<FormQuestionResponse[]>(() => {
    if (!questionsData?.data) return [];
    return Object.values(questionsData.data).flat() as FormQuestionResponse[];
  }, [questionsData]);

  const handleNext = async () => {
    // Validation logic for moving to next step (existing logic)
    let hasError = false;

    if (activeStep === 1) {
      const values = form.getValues();
      const sectionsWithErrors = new Set<string>();

      // Manually validate required IDs
      if (!values.member_id) { form.setError("member_id", { message: "Member is required" }); hasError = true; }
      if (!values.membership_id) { form.setError("membership_id", { message: "Membership is required" }); hasError = true; }
      if (!values.coach_id) { form.setError("coach_id", { message: "Coach is required" }); hasError = true; }
      if (!values.consultation_date) { form.setError("consultation_date", { message: "Date is required" }); hasError = true; }
      if (!values.member_height && values.member_height !== 0) { form.setError("member_height", { message: "Height is required" }); hasError = true; }
      if (!values.member_weight && values.member_weight !== 0) { form.setError("member_weight", { message: "Weight is required" }); hasError = true; }

      allQuestions.forEach((q) => {
        if (q.is_required && q.question_type !== "toggle") {
          const answer = values.answers?.[String(q.id)];
          const isEmpty = 
            answer === undefined || 
            answer === null || 
            answer === "" || 
            (Array.isArray(answer) && answer.length === 0);
          
          if (isEmpty) {
            form.setError(`answers.${q.id}` as FieldPath<Consultation>, { 
              type: "required", 
              message: "Required" 
            });
            hasError = true;
            sectionsWithErrors.add(q.section_key);
          }
        }
      });

      if (sectionsWithErrors.size > 0) {
        setOpenSections(Array.from(sectionsWithErrors));
      }
    } else {
      const isStepValid = await trigger(undefined, { shouldFocus: true });
      if (!isStepValid) hasError = true;
    }

    if (hasError) {
      toast({
        variant: "destructive",
        description: "Please fill all mandatory fields",
      });
      return;
    }

    // Call the exact same save logic as Save button first:
    const saveSuccess = await handleSaveStep();
    if (!saveSuccess) {
      // If saving failed or validation failed in save, do not proceed to next step
      return;
    }

    if (activeStep < steps.length) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handlePrev = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  };

  // Determine if Save button should be enabled
  const isSaveDisabled = 
    isCreating ||
    isUpdating ||
    isSubmittingAnswers ||
    isSavingMeasurement ||
    isSavingBodyAnalysis ||
    isUpdatingBodyAnalysis ||
    isSavingPostureAnalysis ||
    isUpdatingPostureAnalysis ||
    isCreatingGoal ||
    isUpdatingGoal ||
    isAssigningMealPlan ||
    isAssigningWorkoutPlan ||
    isPatchingStatus ||
    isCreatingMealPlan ||
    (activeStep === 3 && isFetchingBodyAnalysis) ||
    (activeStep === 4 && isFetchingPostureAnalysis) ||
    (activeStep === 5 && isFetchingGoal);

  const isSaveLoading =
    isCreating ||
    isUpdating ||
    isSubmittingAnswers ||
    isSavingMeasurement ||
    isSavingBodyAnalysis ||
    isUpdatingBodyAnalysis ||
    isSavingPostureAnalysis ||
    isUpdatingPostureAnalysis ||
    isCreatingGoal ||
    isUpdatingGoal ||
    isAssigningMealPlan ||
    isAssigningWorkoutPlan ||
    isPatchingStatus ||
    isCreatingMealPlan;

  // Determine if the form should be read-only (approved / completed / cancelled)
  const consultationStatus = watch("status");
  const isReadOnly = [
    ConsultationStatus.Approved,
    ConsultationStatus.Completed,
    ConsultationStatus.Rejected,
  ].includes(consultationStatus);

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className="bg-formbg w-full lg:!max-w-[1240px] p-0 custom-scrollbar h-full "
      >
        <FormProvider {...form}>
          <form
            key={action}
            noValidate
            className="pb-4"
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <SheetHeader className="px-3 sm:px-5 sticky top-0 z-40 py-3 sm:py-2 bg-white shadow-sm">
              {/* ── Mobile layout: two rows ── */}
              <div className="flex sm:hidden flex-col gap-2.5 w-full">
                {/* Row 1: close + title + action buttons */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <button type="button" onClick={handleClose} className="shrink-0">
                      <i className="fa fa-xmark text-lg"></i>
                    </button>
                    <h1 className="font-medium text-sm truncate">
                      {action == "add" ? "Create" : "Edit"} Consultation
                    </h1>
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    {isReadOnly && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 uppercase tracking-wide">
                        Read Only
                      </span>
                    )}
                    {!isReadOnly && (
                      <LoadingButton
                        className="h-7 px-2.5 text-xs bg-green-500 hover:bg-green-600"
                        onClick={handleSaveStep}
                        disabled={isSaveDisabled}
                        loading={isSaveLoading}
                        type="button"
                      >
                        {!isSaveLoading && <i className="fa fa-floppy-disk pr-1"></i>}
                        Save
                      </LoadingButton>
                    )}
                    {activeStep !== 1 && (
                      <Button
                        className="h-7 px-2.5 text-xs"
                        variant="outline"
                        onClick={handlePrev}
                        disabled={activeStep === 1}
                        type="button"
                      >
                        Prev
                      </Button>
                    )}
                    {activeStep !== steps.length && (
                      <Button
                        className="h-7 px-2.5 text-xs"
                        variant={isReadOnly ? "outline" : "default"}
                        onClick={() => {
                          if (isReadOnly) {
                            setActiveStep((prev) => Math.min(prev + 1, steps.length));
                          } else {
                            handleNext();
                          }
                        }}
                        disabled={activeStep === steps.length}
                        type="button"
                      >
                        Next
                      </Button>
                    )}
                  </div>
                </div>

                {/* Row 2: scrollable step pills */}
                <div className="overflow-x-auto scrollbar-none -mx-3 px-3">
                  <div className="flex items-center gap-2 w-max py-0.5">
                    {steps.map((step) => (
                      <span
                        key={step.key}
                        className={`shrink-0 text-[11px] font-medium px-2.5 py-0.5 rounded-full border transition-colors ${
                          step.key === activeStep
                            ? "bg-primary text-white border-primary"
                            : step.key < activeStep
                            ? "bg-primary/10 text-primary border-primary/30"
                            : "bg-gray-50 text-gray-400 border-gray-200"
                        }`}
                      >
                        {step.label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Desktop layout: unchanged ── */}
              <div className="hidden sm:flex sm:flex-row sm:items-center justify-between gap-2 w-full">
                <div className="flex items-start gap-3">
                  <button type="button" onClick={handleClose}>
                    <i className="fa fa-xmark text-xl"></i>
                  </button>
                  <div>
                    <h1 className="font-medium">
                      {action == "add" ? "Create" : "Edit"} Consultation
                    </h1>
                    <CompactStepIndicator
                      activeStep={activeStep}
                      steps={steps}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 self-end sm:self-auto">
                  {isReadOnly && (
                    <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-gray-100 text-gray-500 uppercase tracking-wide">
                      Read Only
                    </span>
                  )}
                  {!isReadOnly && (
                    <LoadingButton
                      className="w-[80px]"
                      onClick={handleSaveStep}
                      disabled={isSaveDisabled}
                      loading={isSaveLoading}
                      type="button"
                    >
                      {!isSaveLoading && (
                        <i className="fa fa-floppy-disk pr-2"></i>
                      )}
                      Save
                    </LoadingButton>
                  )}
                  {activeStep !== 1 && (
                    <Button
                      className="w-[80px]"
                      variant="outline"
                      onClick={handlePrev}
                      disabled={activeStep === 1}
                      type="button"
                    >
                      Previous
                    </Button>
                  )}
                  {activeStep !== steps.length && (
                    <Button
                      className="w-[80px]"
                      variant={isReadOnly ? "outline" : "default"}
                      onClick={() => {
                        if (isReadOnly) {
                          setActiveStep((prev) => Math.min(prev + 1, steps.length));
                        } else {
                          handleNext();
                        }
                      }}
                      disabled={activeStep === steps.length}
                      type="button"
                    >
                      Next
                    </Button>
                  )}
                </div>
              </div>
            </SheetHeader>

            <SheetDescription className="h-full p-6">
              <div className="flex flex-col space-y-4">
                {/* ✅ ADD HISTORY FILTER AT TOP */}
                {activeStep === 1 && (
                  <HistoryFilter 
                    memberId={watch("member_id")!} 
                    onDataFound={setSharedHistoryData}  // ✅ Use shared state
                  />
                )}
                {/* Form Content - Scrollable on the right */}
                <div className="flex-1">
                  {isReadOnly ? (
                    <fieldset disabled className="contents [&_*]:!cursor-default">
                      {getStepContent(activeStep, { action, open: isOpen, setAction, refetch, openSections, setOpenSections, historyData: sharedHistoryData, isReadOnly })}
                    </fieldset>
                  ) : (
                    getStepContent(activeStep, { action, open: isOpen, setAction, refetch, openSections, setOpenSections, historyData: sharedHistoryData, isReadOnly: false })
                  )}
                </div>
              </div>
            </SheetDescription>
          </form>
        </FormProvider>
        <AlertDialog open={showWarning} onOpenChange={setShowWarning}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
              <AlertDialogDescription>
                You have unsaved changes. Are you sure you want to close? Your
                changes will be lost.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={forceClose} className="bg-destructive hover:bg-destructive/90">
                Discard
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
};

export default ConsultationForm;